import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import Map, {
  Layer,
  MapProvider,
  Source,
  MapLayerMouseEvent,
  MapboxGeoJSONFeature,
} from "react-map-gl";
import {
  permitSubmittedLayer,
  permitApprovedLayer,
  baseLayer,
} from "./MapLayers";
import CustomisedCard from "../CustomisedCard";
import type { MapRef } from "react-map-gl";
import type { MapboxEvent } from "mapbox-gl";
import InformationModal from "../InformationModal";
import WardLabels from "../../data/wardLabels";
import ClusteredLayer from "../../layers/ClusterLayers";
import { notifications } from "@mantine/notifications";
import { BOUNDS, DEFAULT_VIEW, MAPBOX_STYLE, MAPBOX_TOKEN } from "../../constants/mapbox.constants";
import { useMapboxImages } from "../../hooks/useMapboxImages";
import { fetchDatasetsAndMatchProperties } from "../../utils/mapboxDataFetcherUtil";
import { updatePinLineData } from "../../utils/mapboxPinLineUtil";
import { MapControls } from "./MapControls";
import { LayerVisibility } from '../../types/mapbox.types';
import { throttle } from "lodash";

/** Zoom UI + pin geometry (offsets depend on zoom). */
const SYNC_ZOOM_INTERVAL_MS = 100;
/** Pan-only rebuild: lines follow viewport; slightly looser avoids stacking work during drag. */
const PIN_LINES_ON_MOVE_MS = 180;

const Mapbox: React.FC = () => {
  const mapRef = useRef<MapRef>(null);

  const [selectedFeature, setSelectedFeature] =
    useState<MapboxGeoJSONFeature | null>(null);
  const [showSideBar, setShowSideBar] = useState<Boolean>(false);

  const [geojsonData, setGeojsonData] = useState<any>(null);
  const [POIData, setPOIData] = useState<any>(null);
  const [POILineData, setPOILineData] = useState<any>(null);
  const [boundaryData, setBoundaryData] = useState<any>(null);
  const [isPinned, setIsPinned] = useState(true);
  const [isWardLabel, setIsWardLabel] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<any>(DEFAULT_VIEW.zoom);
  const minimumApplicationsToShowCluster = 20;

  const wardLabels = WardLabels.getData();

  
  const [layersVisibility, setLayersVisibility] = React.useReducer(
    (state: LayerVisibility, updates: Partial<LayerVisibility>) => ({ ...state, ...updates }),
    {} as LayerVisibility
  );

  const handleMapClick = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features?.[0];
    if (feature) {
      setShowSideBar(true);
      setSelectedFeature(feature);
    } else {
      setShowSideBar(false);
    }
  }, []);

  const pinButtonHandle = useCallback(() => {
    setIsPinned((prev) => !prev);
  }, []);

  const wardLabelButtonHandle = useCallback(() => {
    setIsWardLabel((prev) => !prev);
  }, []);

  const handleMouseMove = useCallback((event: MapLayerMouseEvent) => {
    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }

    const features = map.queryRenderedFeatures(event.point, {
      layers: ["permitSubmitted", "permitApproved"],
    });

    if (features.length > 0) {
      // Set cursor style to pointer
      map.getCanvas().style.cursor = "pointer";
    }
  }, []);

  const { preloadImages } = useMapboxImages();

  const handleMapLoad = useCallback(
    (e: MapboxEvent) => {
      void preloadImages(e.target);
    },
    [preloadImages]
  );

  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // Reset cursor style
    map.getCanvas().style.cursor = "";
  }, []);

  const flyToHome = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      map.flyTo({
        center: DEFAULT_VIEW.centre,
        zoom: DEFAULT_VIEW.zoom,
        pitch: DEFAULT_VIEW.pitch,
        bearing: DEFAULT_VIEW.bearing,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      });
    }
  }, []);

  // Zoom for UI/state — skip React updates when unchanged (still true during pan/move).
  const handleZoomChange = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const next = map.getZoom();
    setZoomLevel((prev: number) =>
      typeof prev === "number" && Math.abs(prev - next) < 1e-6 ? prev : next
    );
  }, []);


  const updatePinLineDataCallback = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();
    const z = map.getZoom();
    const newPinLineData = updatePinLineData(map, POIData, z);

    if (newPinLineData) {
      setPOILineData(newPinLineData);
    }
  }, [POIData]);


  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    // zoom: refresh zoom label when it changes, and rebuild pin connectors (tiered offsets).
    const onZoomThrottled = throttle(() => {
      handleZoomChange();
      updatePinLineDataCallback();
    }, SYNC_ZOOM_INTERVAL_MS);

    // move: zoom value is unchanged during pan — only rebuild lines; looser throttle than zoom.
    const onMovePinsThrottled = throttle(
      () => {
        updatePinLineDataCallback();
      },
      PIN_LINES_ON_MOVE_MS,
      { leading: true, trailing: true }
    );

    const runInitialSync = () => {
      handleZoomChange();
      updatePinLineDataCallback();
    };
    runInitialSync();

    map.on("zoom", onZoomThrottled);
    map.on("move", onMovePinsThrottled);

    return () => {
      map.off("zoom", onZoomThrottled);
      map.off("move", onMovePinsThrottled);
      onZoomThrottled.cancel();
      onMovePinsThrottled.cancel();
    };
  }, [updatePinLineDataCallback, handleZoomChange]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const { GeojsonData, POIData, BoundaryData } = await fetchDatasetsAndMatchProperties();
        setGeojsonData(GeojsonData);
        setPOIData(POIData);
        setBoundaryData(BoundaryData);
      } catch (error) {
        console.error('Error loading data:', error);
        const message =
          error instanceof Error
            ? error.message
            : "Something went wrong while loading map data.";
        notifications.show({
          title: "Couldn't load map data",
          message,
          color: "red",
          autoClose: 20_000,
          withCloseButton: true,
        });
      }
    };

    loadData();
  }, []);

  return (
    <MapProvider>
      <Map
        ref={mapRef}
        initialViewState={DEFAULT_VIEW}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={MAPBOX_STYLE}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        maxZoom={23}
        interactiveLayerIds={[
          "custom-threebox-model-2",
          "custom-threebox-model",
          "permitSubmitted",
          "permitApproved",
        ]}
        maxBounds={BOUNDS}
      >
        <InformationModal />
        <MapControls
          isWardLabel
          isPinned
          zoomLevel={zoomLevel}
          onWardLabelToggle={wardLabelButtonHandle}
          onPinToggle={pinButtonHandle}
          onHomeClick={flyToHome}
          onFilterSet={setLayersVisibility}
        />

        {showSideBar ? (
          <aside className="sidebar">
            <CustomisedCard
              stage={selectedFeature?.properties?.ApplicationStage}
              title={selectedFeature?.properties?.SiteName}
              status={selectedFeature?.properties?.ApplicationStatus}
              applicationURL={selectedFeature?.properties?.ApplicationUrl}
            >
              {" "}
              <p>
                <b>Stage details:</b>{" "}
                {selectedFeature?.properties?.ApplicationStatus}
              </p>
              <p>
                <b>Description:</b> {selectedFeature?.properties?.Description}
              </p>
              {/* <p>
                    <b>Planning Permit Number:</b> {selectedFeature?.properties?.FormattedTitle}
                  </p> */}
            </CustomisedCard>
          </aside>
        ) : null}
        <Source
          id="St_Kilda_Road"
          type="geojson"
          data={geojsonData}
        // url="mapbox://coppcdam.cltoxkepc007d1vt1bslqhke0-6ncwq"
        >
          <Layer
            {...permitSubmittedLayer}
            layout={{ visibility: layersVisibility["permitSubmitted"] }}
          />
          <Layer {...baseLayer} />
          <Layer
            {...permitApprovedLayer}
            layout={{ visibility: layersVisibility["permitApproved"] }}
          />
        </Source>
        {isPinned && (zoomLevel <= 15 && ( zoomLevel >= 14 || POIData?.features?.length < minimumApplicationsToShowCluster)) && (
          <div>
            <Source id="poi-data" type="geojson" data={POIData}>
              <Layer
                id="poi-layer"
                type="symbol"
                layout={{
                  "icon-image": ["get", "icon"], // Use the custom marker
                  "icon-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10,
                    0.1, // Icon size at zoom level 10
                    15,
                    0.17, // Icon size at zoom level 15
                    20,
                    0.2, // Icon size at zoom level 20
                    24,
                    0.25, // Icon size at zoom level 24
                  ],
                  "symbol-z-order": "viewport-y",
                  "icon-allow-overlap": true,
                  visibility: layersVisibility["permitSubmitted"],
                }}
                paint={{
                  "icon-opacity": 1,
                  "icon-translate": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10,
                    [0, -50], // At zoom level 10, translate by -30
                    15,
                    [0, -80], // At zoom level 15, translate by -60
                    20,
                    [0, -120], // At zoom level 20, translate by -120
                    24,
                    [0, -180], // At zoom level 24, translate by -180
                  ],
                  "icon-translate-anchor": "viewport", // Ensure the translation is relative to the viewport
                }}
                filter={[
                  "in",
                  "ApplicationStage",
                  "INITASSESS",
                  "PAYMENTRECEIVED",
                  "PUBNOTIFY",
                  "INFREQUEST",
                  "ATPRPTNG",
                  "S5057ALODG",
                  "REFERRED",
                  "UNDERASSES",
                  "CON1PLAN",
                  "COND1RECVD",
                  "COORDREVIEW",
                  "ADVTPREP",
                ]}
              />
              <Layer
                id="poi-layer-1"
                type="symbol"
                layout={{
                  "icon-image": ["get", "icon"], // Use the custom marker
                  "icon-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10,
                    0.1, // Icon size at zoom level 10
                    15,
                    0.17, // Icon size at zoom level 15
                    20,
                    0.2, // Icon size at zoom level 20
                    24,
                    0.25, // Icon size at zoom level 24
                  ],
                  "symbol-z-order": "viewport-y",
                  "icon-allow-overlap": true,
                  visibility: layersVisibility["permitApproved"],
                }}
                paint={{
                  "icon-opacity": 1,
                  "icon-translate": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10,
                    [0, -50], // At zoom level 10, translate by -30
                    15,
                    [0, -80], // At zoom level 15, translate by -60
                    20,
                    [0, -120], // At zoom level 20, translate by -120
                    24,
                    [0, -180], // At zoom level 24, translate by -180
                  ],
                  "icon-translate-anchor": "viewport", // Ensure the translation is relative to the viewport
                }}
                filter={[
                  "in",
                  "ApplicationStage",
                  "APPROVED",
                  "PMTISSUED",
                  "ISSUED",
                ]}
              />
            </Source>
            {POILineData && (
              <Source id="line-data" type="geojson" data={POILineData}>
                <Layer
                  id="line-layer"
                  type="line"
                  layout={{
                    "line-join": "round",
                    "line-cap": "round",
                    visibility: layersVisibility["permitSubmitted"],
                  }}
                  paint={{
                    "line-color": "#898989",
                    "line-width": 2,
                    "line-opacity": 0.6,
                  }}
                  // Insert this layer before the "poi-layer" and "extrusion-layer"
                  beforeId="poi-layer" // Replace with the ID of the layer you want it to render behind
                  filter={[
                    "in",
                    "ApplicationStage",
                    "INITASSESS",
                    "PAYMENTRECEIVED",
                    "PUBNOTIFY",
                    "INFREQUEST",
                    "ATPRPTNG",
                    "S5057ALODG",
                    "REFERRED",
                    "UNDERASSES",
                    "CON1PLAN",
                    "COND1RECVD",
                    "COORDREVIEW",
                    "ADVTPREP",
                  ]}
                />
                <Layer
                  id="line-layer-2"
                  type="line"
                  layout={{
                    "line-join": "round",
                    "line-cap": "round",
                    visibility: layersVisibility["permitApproved"],
                  }}
                  paint={{
                    "line-color": "#898989",
                    "line-width": 2,
                    "line-opacity": 0.6,
                  }}
                  beforeId="poi-layer"
                  filter={[
                    "in",
                    "ApplicationStage",
                    "APPROVED",
                    "PMTISSUED",
                    "ISSUED",
                  ]}
                />
              </Source>
            )}
          </div>
        )}

        {isPinned && zoomLevel <= 14 && POIData?.features?.length >= minimumApplicationsToShowCluster && <ClusteredLayer data={POIData} />}

        {isWardLabel && (
          <>
            <Source id="ward-labels" type="geojson" data={wardLabels}>
              <Layer
                id="ward-label-layer"
                type="symbol"
                layout={{
                  "text-field": ["get", "name"],
                  "text-size": 14,
                  "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                  "text-offset": [0, 1],
                  "text-anchor": "top",
                }}
                paint={{
                  "text-color": "#333333",
                  "text-halo-color": "#ffffff",
                  "text-halo-width": 1,
                }}
              />
            </Source>
            <Source
              id="local-gov-boundaries"
              type="geojson"
              data={boundaryData}
            >
              <Layer
                id="boundary-layer"
                type="line"
                paint={{
                  "line-color": "#6E6E6E",
                  "line-width": 2,
                  "line-opacity": 1,
                }}
              />
            </Source>
          </>
        )}
      </Map>
    </MapProvider>
  );
};

export default Mapbox;
